import { Box, Popover, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "rsuite";
import BriaInput from "../../../../../../components/common/BriaInput/BriaInput";
import { useActiveObject } from "../../../../../hooks/useActiveObject";
import { useEditor } from "../../../../../hooks/useEditor";
import { ILayer } from "../../../../../types";
import { ObjectsEnum } from "../../Panels/panelItems";
import styles from "./FabricObjectsPropertiesPopover.module.scss";
type Props = {
	open: boolean;
	handleClose?: () => void;
	anchorEl?: HTMLButtonElement;
};

const FabricObjectsPropertiesPopover = ({ open = false, handleClose, anchorEl }: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "editor.controllers.textControllers" });
	const activeObject = useActiveObject() as Required<fabric.Object>;
	const editor = useEditor();
	const [currentLayer, setCurrentLayer] = useState<Partial<ILayer> | undefined>(undefined);
	const [activeObjectTopValue, setActiveObjectTopValue] = useState<number | undefined>(activeObject?.top);
	const [activeObjectLeftValue, setActiveObjectLeftValue] = useState<number | undefined>(activeObject?.left);

	useEffect(() => {
		let watcher = async () => {
			updateCurrentLayerState();
		};
		if (editor) {
			editor.on("history:changed", watcher);
		}
		return () => {
			if (editor) {
				editor.off("history:changed", watcher);
			}
		};
	}, [editor, activeObject]);

	useEffect(() => {
		updateCurrentLayerState();
	}, [editor, activeObject]);

	const updateCurrentLayerState = () => {
		if (editor && activeObject) {
			const currentScene = editor.scene.exportToJSON();
			let layer = currentScene?.layers.find((layer) => layer.id === activeObject.id);
			if (!layer && activeObject.id === ObjectsEnum.FRAME) {
				layer = currentScene?.layers.find((layer) => layer.id === ObjectsEnum.InitialFrame);
			}
			setCurrentLayer(layer);
			setActiveObjectLeftValue(layer?.left);
			setActiveObjectTopValue(layer?.top);
		}
	};

	return (
		<Popover
			disableRestoreFocus
			open={open}
			onClose={handleClose}
			anchorEl={anchorEl}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "left",
			}}
			slotProps={{
				paper: {
					className: styles.popover,
				},
			}}
		>
			<Box className={styles.propertiesContainer}>
				<Typography className={styles.proportiesTitle}>{t("position")}</Typography>
				<Box className={styles.properties}>
					<Box className={styles.inputLabelContainer}>
						<Typography>{t("xLabel")}</Typography>
						<BriaInput
							rootClass={styles.rootClass}
							value={activeObjectLeftValue && Math.round(activeObjectLeftValue)}
							onChange={(e) => {
								const newValue = Number(e.target.value);
								setActiveObjectLeftValue(newValue);
								editor?.objects.update({ left: newValue });
							}}
							type="number"
							customEndAdornmentText="px"
						/>
					</Box>
					<Box className={styles.inputLabelContainer}>
						<Typography>{t("yLabel")}</Typography>
						<BriaInput
							rootClass={styles.rootClass}
							value={activeObjectTopValue && Math.round(activeObjectTopValue)}
							onChange={(e) => {
								const newValue = Number(e.target.value);

								setActiveObjectTopValue(newValue);
								editor?.objects.update({ top: newValue });
							}}
							type="number"
							customEndAdornmentText="px"
						/>
					</Box>
				</Box>
				<Divider className={styles.divider} />
				<Typography className={styles.proportiesTitle}>{t("dimensions")}</Typography>
				<Box className={styles.properties}>
					<Box className={styles.widthInputLabelContainer}>
						<Typography>{t("widthLabel")}</Typography>
						<BriaInput
							disabled
							rootClass={styles.rootClass}
							value={currentLayer?.width && Math.round(currentLayer.width)}
							onChange={() => {}}
							type="number"
							customEndAdornmentText="px"
						/>
					</Box>
					<Box className={styles.inputLabelContainer}>
						<Typography>{t("heightLabel")}</Typography>
						<BriaInput
							disabled
							rootClass={styles.rootClass}
							value={currentLayer?.height && Math.round(currentLayer.height)}
							onChange={() => {}}
							type="number"
							customEndAdornmentText="px"
						/>
					</Box>
				</Box>
			</Box>
		</Popover>
	);
};

const ObservedComponent = observer(FabricObjectsPropertiesPopover);
export default ObservedComponent;
